import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComparisonPanel = ({ schools, onClose }) => {
  const comparisonCategories = [
    { key: 'type', label: 'Type', icon: 'Building' },
    { key: 'location', label: 'Localisation', icon: 'MapPin' },
    { key: 'studentCount', label: 'Nombre d\'étudiants', icon: 'Users' },
    { key: 'accreditation', label: 'Accréditation', icon: 'Award' },
    { key: 'rating', label: 'Note', icon: 'Star' },
    { key: 'tuitionFees', label: 'Frais de scolarité', icon: 'Coins' },
    { key: 'admissionRequirements', label: 'Conditions d\'admission', icon: 'ClipboardCheck' }
  ];

  const formatValue = (key, value, school) => {
    if (key === 'type') return value === 'public' ? 'Public' : 'Privé';
    if (key === 'studentCount') return value?.toLocaleString();
    if (key === 'rating') return `${value}/5 (${school?.reviewCount} avis)`;
    return value;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Icon name="GitCompare" size={28} color="var(--color-primary)" />
            Comparaison des Établissements
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Icon name="X" size={24} color="#6B7280" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 md:p-6">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-white z-10 p-3 text-left border-b-2 border-gray-200">
                    <span className="text-sm font-semibold text-gray-600">Critère</span>
                  </th>
                  {schools?.map((school) => (
                    <th key={school?.id} className="p-3 border-b-2 border-gray-200 min-w-[250px]">
                      <div className="text-left">
                        <h3 className="font-semibold text-gray-800 mb-1">{school?.name}</h3>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            school?.type === 'public' ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-blue-600'
                          }`}>
                            {school?.type === 'public' ? 'Public' : 'Privé'}
                          </span>
                          <div className="flex items-center gap-1">
                            <Icon name="Star" size={12} color="#F59E0B" fill="#F59E0B" />
                            <span className="text-xs text-gray-600">{school?.rating}</span>
                          </div>
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonCategories?.map((category, index) => (
                  <tr key={category?.key} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="sticky left-0 bg-inherit z-10 p-3 border-b border-gray-200">
                      <div className="flex items-center gap-2">
                        <Icon name={category?.icon} size={16} color="#6B7280" />
                        <span className="text-sm font-medium text-gray-700">{category?.label}</span>
                      </div>
                    </td>
                    {schools?.map((school) => (
                      <td key={school?.id} className="p-3 border-b border-gray-200">
                        <span className="text-sm text-gray-800">
                          {formatValue(category?.key, school?.[category?.key], school)}
                        </span>
                      </td>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td className="sticky left-0 bg-white z-10 p-3 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      <Icon name="BookOpen" size={16} color="#6B7280" />
                      <span className="text-sm font-medium text-gray-700">Programmes</span>
                    </div>
                  </td>
                  {schools?.map((school) => (
                    <td key={school?.id} className="p-3 border-b border-gray-200">
                      <div className="flex flex-wrap gap-1">
                        {school?.programs?.slice(0, 3)?.map((program, idx) => (
                          <span key={idx} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                            {program}
                          </span>
                        ))}
                        {school?.programs?.length > 3 && (
                          <span className="text-xs text-gray-500">+{school?.programs?.length - 3}</span>
                        )}
                      </div>
                    </td>
                  ))}
                </tr>
                <tr className="bg-gray-50">
                  <td className="sticky left-0 bg-gray-50 z-10 p-3 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      <Icon name="Wrench" size={16} color="#6B7280" />
                      <span className="text-sm font-medium text-gray-700">Infrastructures</span>
                    </div>
                  </td>
                  {schools?.map((school) => (
                    <td key={school?.id} className="p-3 border-b border-gray-200">
                      <ul className="text-sm text-gray-700 space-y-1">
                        {school?.facilities?.slice(0, 3)?.map((facility, idx) => (
                          <li key={idx} className="flex items-start gap-1">
                            <Icon name="Check" size={14} color="#10B981" className="mt-0.5" />
                            <span>{facility}</span>
                          </li>
                        ))}
                      </ul>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 md:p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            <Button variant="outline" onClick={onClose}>
              Fermer
            </Button>
            <Button variant="default" iconName="Download" iconPosition="left">
              Exporter la comparaison
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonPanel;